import { createSlice } from '@reduxjs/toolkit';
import { makeFetchSuccessActionType } from '../../event/fetchActionTypeProvider';
import { HttpStatusCode } from '../../fetch/core/constant';
import { isEmpty } from '../../utils';
import { sendGlobalEvent } from '../../utils/emit';
import { translateToCh } from '../../utils/translateComponentName';
import * as AllAction from '../action';
import ActionType, {
  actionFetchBasicInfo,
  actionMergeAlarmToMenu,
} from '../action/common';
import { ConnectionState } from '../type';
import { enableMapSet } from 'immer';

enableMapSet();

type AlarmEventItem = {
  vehicleName: string;
  alarmEvent: string;
  reportTime: string;
};

const alarmEventBuffers: any = {};
const vehicleStateBuffers: any = {};
const initialState: any = {
  enumListMap: null,
  basicInfoTimeStamp: 0,
  realTimeBoardData: {
    timeStamp: 0,
  },
  alarmEventList: {},
  issueProcessEventList: [],
  issueFinishedEventList: [],
  connectionState: ConnectionState.LOST,
  city: {},
  station: {},
  vehicle: {},
  filterVehicleType: 'ALL', // 默认展示全部车辆
  alarmAndOpenration: null, //  工单处理选中的报警列表和操作记录
  realTimeVehiclesPosition: {
    timeStamp: 0,
    data: {},
  },
  mapScheduleUpdate: {
    timeStamp: 0,
  },
  vehicleStartUpEnum: new Map(),
};

const formatAlarmEventList = (state: any) => {
  // 计算菜单上的异常数量
  const stationObj: any = {};
  const cityObj: any = {};
  const vehicleObj: any = {};
  Object.values(state.alarmEventList).forEach((item: any) => {
    const { vehicleName } = item;
    const vehicle = state.vehicle[vehicleName];
    if (!vehicle) {
      return;
    }
    vehicleObj[vehicleName] = {};
    vehicleObj[vehicleName].state = 'ABNORMAL';
    if (stationObj[vehicle.pid]) {
      stationObj[vehicle.pid].abnormalNum++;
    } else {
      stationObj[vehicle.pid] = {};
      stationObj[vehicle.pid].abnormalNum = 1;
    }

    if (cityObj[state.station[vehicle.pid].pid]) {
      cityObj[state.station[vehicle.pid].pid].abnormalNum++;
    } else {
      cityObj[state.station[vehicle.pid].pid] = {};
      cityObj[state.station[vehicle.pid].pid].abnormalNum = 1;
    }
  });
  for (const stationId in state.station) {
    if (stationObj[stationId]) {
      state.station[stationId].abnormalNum = stationObj[stationId].abnormalNum;
    } else {
      state.station[stationId].abnormalNum = 0;
    }
  }

  for (const cityId in state.city) {
    if (cityObj[cityId]) {
      state.city[cityId].abnormalNum = cityObj[cityId].abnormalNum;
    } else {
      state.city[cityId].abnormalNum = 0;
    }
  }

  for (const vehicleKey in state.vehicle) {
    if (vehicleObj[vehicleKey]) {
      state.vehicle[vehicleKey].vehicleState = vehicleObj[vehicleKey].state;
    } else {
      state.vehicle[vehicleKey].vehicleState =
        state.vehicle[vehicleKey].originState;
    }
  }
};

const commonState = createSlice({
  name: 'common',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(AllAction.actionConnectionOk, (state, action) => {
        state.connectionState = ConnectionState.OK;
      })
      .addCase(AllAction.actionConnectionLost, (state, action) => {
        state.connectionState = ConnectionState.LOST;
      })
      .addCase(actionFetchBasicInfo, (state, action: any) => {
        const res = action.payload;
        state.city = {};
        state.station = {};
        state.vehicle = {};
        state.basicInfoTimeStamp = Date.now();
        res.data?.forEach((item: any, index: number) => {
          const { station, ...otherData } = item;
          state.city[otherData.id] = otherData;
          state.city[otherData.id].abnormalNum = 0;
          state.city[otherData.id].stationNum = station.length;
          state.city[otherData.id].lat = station && station[0].lat;
          state.city[otherData.id].lon = station && station[0].lon;
          station.forEach((s: any, i: number) => {
            const { vehicle, ...other } = s;
            state.station[other.id] = other;
            state.station[other.id].abnormalNum = 0;
            state.station[other.id].pid = otherData.id;
            vehicle.forEach((v: any, k: number) => {
              state.vehicle[v.name] = v || {};
              state.vehicle[v.name].pid = other.id;
              state.vehicle[v.name].gid = otherData.id;
              state.vehicle[v.name].stationName = other.name;
              state.vehicle[v.name].originState = v.vehicleState;
              state.vehicle[v.name].attentionState = v.attentionState;
            });
            state.station[other.id].attentionState = Object.values(
              state.vehicle,
            ).includes((v: any) => v.attentionState);
          });
          state.city[otherData.id].attentionState = Object.values(
            state.station,
          ).includes((s: any) => s.attentionState);
        });
        if (Object.keys(state.alarmEventList).length > 0) {
          formatAlarmEventList(state);
        }
      })
      .addCase(AllAction.actionMultiVehicle, (state, action) => {
        state.vehicle = action.payload;
      })
      .addCase(AllAction.actionMultiStation, (state, action) => {
        state.station = action.payload;
      })
      .addCase(AllAction.actionMultiCity, (state, action) => {
        state.city = action.payload;
      })
      .addCase(
        makeFetchSuccessActionType(ActionType.MULTI_VEHICLE),
        (state, action: any) => {
          const res = action.payload;
          state.realTimeBoardData = {
            ...res.data,
            timeStamp: Date.now(),
          };
        },
      )
      .addCase(AllAction.actionTokenInValid, (state, action: any) => {
        state.city = {};
        state.station = {};
        state.vehicle = {};
      })
      .addCase(AllAction.actionChangeFilterType, (state, action: any) => {
        state.city = {};
        state.station = {};
        state.vehicle = {};
        state.filterVehicleType = action.payload;
      })
      .addCase(AllAction.actionAlarmDetailInfo, (state, action: any) => {
        const resp = action.payload;
        if (resp.errorCode != 0 || !resp.data) {
          return;
        }
        state.alarmAndOpenration = resp.data;
      })
      .addCase(AllAction.actionClearAlarmDetailInfo, (state) => {
        state.alarmAndOpenration = null;
      })
      .addCase(
        makeFetchSuccessActionType(ActionType.USER_METADATA_CITY),
        (state, action: any) => {
          const resp = action.payload;
          if (resp.code != '0000' || !resp.data) {
            return;
          }
          state.userCity = resp.data;
        },
      )
      .addCase(
        makeFetchSuccessActionType(ActionType.USER_METADATA_VEHICLE),
        (state, action: any) => {
          const resp = action.payload;
          if (resp.code != '0000' || !resp.data) {
            return;
          }
          state.userVehicle = resp.data;
        },
      )
      .addCase(
        makeFetchSuccessActionType(ActionType.ALL_ALARM_EVENT),
        (state, action: any) => {
          const res = action.payload;
          if (res.code === HttpStatusCode.Success) {
            res.data &&
              res.data.forEach((item: any) => {
                state.alarmEventList[item.vehicleName] = item;
              });
            formatAlarmEventList(state);
          }
        },
      )
      .addCase(
        makeFetchSuccessActionType(ActionType.CHANGE_ALARM_EVENT),
        (state, action: any) => {
          const res = action.payload;
          if (!isEmpty(res.data)) {
            res.data.forEach((item: AlarmEventItem) => {
              alarmEventBuffers[item.vehicleName] = item;
            });
          }
        },
      )
      .addCase(
        makeFetchSuccessActionType(ActionType.ISSUE_SUBSCRIBE_REQUEST),
        (state, action: any) => {
          const res = action.payload;
          if (res) {
            state.issueProcessEventList = res.data.process;
            state.issueFinishedEventList = res.data.finished;
          }
        },
      )
      .addCase(
        makeFetchSuccessActionType(ActionType.ISSUE_UPDATE_EVENT),
        (state, action: any) => {
          const res = action.payload;
          switch (res.data.issueState) {
            case 'WAITING':
              state.issueProcessEventList = [
                ...state.issueProcessEventList,
                { ...res.data },
              ];
              break;
            case 'PROCESS': {
              const addOrUpdate = state.issueProcessEventList.find(
                (item: any) => item.issueNo === res.data.issueNo,
              );
              if (addOrUpdate) {
                const newIssue = state.issueProcessEventList.map(
                  (item: any) => {
                    return item.issueNo === res.data.issueNo ? res.data : item;
                  },
                );
                state.issueProcessEventList = newIssue;
              } else {
                state.issueProcessEventList = [
                  ...state.issueProcessEventList,
                  { ...res.data },
                ];
              }
              break;
            }
            case 'FINISH': {
              const tempIssueProcess = state.issueProcessEventList;
              const isDelateIssue = tempIssueProcess.filter((item: any) => {
                if (item.issueNo !== res.data.issueNo) {
                  return item;
                }
              });
              state.issueProcessEventList = isDelateIssue;
              state.issueFinishedEventList = [
                ...state.issueFinishedEventList,
                { ...res.data },
              ];
              break;
            }
            case 'UNREPORTED': {
              const isDelateIssue = state.issueFinishedEventList.filter(
                (item: any) => {
                  if (item.issueNo !== res.data.issueNo) {
                    return item;
                  }
                },
              );
              state.issueFinishedEventList = isDelateIssue;
              break;
            }
            case 'REPORTED': {
              const isDelateIssue = state.issueFinishedEventList.filter(
                (item: any) => {
                  if (item.issueNo !== res.data.issueNo) {
                    return item;
                  }
                },
              );
              state.issueFinishedEventList = isDelateIssue;
              break;
            }
          }
        },
      )
      .addCase(actionMergeAlarmToMenu, (state, action: any) => {
        const buffers = Object.values(alarmEventBuffers);
        if (!isEmpty(buffers)) {
          buffers.forEach((item: any) => {
            if (!item.alarmEvent) {
              delete state.alarmEventList[item.vehicleName];
            } else {
              state.alarmEventList[item.vehicleName] = item;
            }
            delete alarmEventBuffers[item.vehicleName];
          });
          formatAlarmEventList(state);
        }
        // eslint-disable-next-line guard-for-in
        for (const key in vehicleStateBuffers) {
          const value = vehicleStateBuffers[key];
          if (state.vehicle[key]) {
            state.vehicle[key] = Object.assign({}, state.vehicle[key], {
              vehicleState: value,
              originState: value,
            });
          }
        }
      })
      .addCase(
        makeFetchSuccessActionType(ActionType.GET_ENUM_LIST_MAP),
        (state, action: any) => {
          const res = action.payload;
          if (res.code === HttpStatusCode.Success) {
            state.enumListMap = res.data;
            localStorage.setItem('ENUM_LIST_MAP', JSON.stringify(res.data));
          }
        },
      )
      .addCase(
        makeFetchSuccessActionType(ActionType.CHANGE_CONNECT),
        (state, action: any) => {
          const res = action.payload;
          if (res.code === HttpStatusCode.Success) {
            const data = res.data || {};
            const { vehicleName, systemState } = data;
            const timer = setTimeout(() => {
              clearTimeout(timer);
              if (vehicleName) {
                let vehicleState = '';
                if (['SELF_REPAIR', 'MALFUNCTION'].includes(systemState)) {
                  vehicleState = 'NORMAL';
                } else if (['UNKNOWN', 'OFFLINE'].includes(systemState)) {
                  vehicleState = 'OFFLINE';
                } else {
                  vehicleState = systemState;
                }
                vehicleStateBuffers[vehicleName] = vehicleState;
              }
            }, 1000);
          }
        },
      )
      .addCase(
        makeFetchSuccessActionType(ActionType.MAP_VEHICLE_POSITION_REQUEST),
        (state, action: any) => {
          const res = action.payload;
          if (res.code === HttpStatusCode.Success) {
            state.realTimeVehiclesPosition.timeStamp = Date.now();
            state.realTimeVehiclesPosition.data = res.data || {};
          }
        },
      )
      .addCase(
        makeFetchSuccessActionType(ActionType.MAP_VEHICLE_SCHEDULE_UPDATE),
        (state, action: any) => {
          const res = action.payload;
          if (res.code === HttpStatusCode.Success) {
            state.mapScheduleUpdate.timeStamp = Date.now();
          }
        },
      )
      .addCase(
        makeFetchSuccessActionType(ActionType.COMMON_MESSAGE),
        (state, action: any) => {
          const res = action.payload;
          if (res.code === HttpStatusCode.Success) {
            state.mapScheduleUpdate.timeStamp = Date.now();
            const { vehicleName, messageLevel, title, messageText } = res.data;
            const obj = {
              timeStamp: Date.now(),
              messageLevel,
              title,
              messageText,
            };
            sendGlobalEvent('Control_Message', obj);
          }
        },
      )
      .addCase(
        makeFetchSuccessActionType(ActionType.VEHICLE_START_UP),
        (state, action: any) => {
          const res = action.payload;
          if (res.code === 'OK') {
            res.data.list.forEach((item: any) => {
              state.vehicleStartUpEnum.set(
                item.device + item.moduleField,
                item,
              );
            });
          }
        },
      );
  },
});

export default commonState.reducer;
